import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, FileText } from 'lucide-react';
import { ProductCard, imgSrc } from '../components/shop/ProductCard';
import { PaymentModal } from '../components/shop/PaymentModal';
import { SampleModal } from '../components/shop/SampleModal';
import { ShareBar } from '../components/ShareBar';
import { regionSlug } from '../lib/regions';
import { DestinationAlertForm } from '../components/studio/TownAlertForm';
import { useSeo } from '../lib/seo';
import { FaqSection } from '../components/shop/FaqSection';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Group = ({ title, items, onBuy, testId }) => items.length === 0 ? null : (
  <div className="mb-16" data-testid={testId}>
    <p className="eyebrow mb-6"><span className="w-10 h-px bg-[#C5A059]" /> {title}</p>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">{items.map((p) => <ProductCard key={p.id} product={p} onBuy={onBuy} />)}</div>
  </div>
);

export default function DestinationPage() {
  const { slug } = useParams();
  const [products, setProducts] = useState(null);
  const [buying, setBuying] = useState(null);
  const [sample, setSample] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${BACKEND}/api/products`).then((r) => setProducts(r.data.products || [])).catch(() => setProducts([]));
  }, [slug]);

  const items = (products || []).filter((p) => p.source === 'guide_studio' && regionSlug(p.destination) === slug);
  const lead = items.find((p) => p.product_key === 'bundle') || items[0];
  useSeo(lead && { title: `Moving from Australia to ${lead.destination}: Relocation Guides`, description: lead.guide_subtitle ? `${lead.guide_subtitle}. ${lead.tagline || ''}` : lead.description, image: lead.image_url }, [lead?.id]);

  if (!products) return <div className="min-h-screen pt-32 flex justify-center"><Loader2 className="animate-spin text-[#3B164C]" size={40} /></div>;
  if (!lead) return <div className="min-h-screen pt-32 text-center" data-testid="destination-not-found"><p className="text-[#1E1622]/60">We don't have guides for this destination yet.</p><Link to="/shop" className="link-gold mt-4 inline-block">Browse the shop</Link></div>;

  const full = items.filter((p) => ['bundle', 'complete'].includes(p.product_key));
  const parts = items.filter((p) => !['bundle', 'complete'].includes(p.product_key));
  const facts = lead.fees?.marketing_lines || [];
  return (
    <div className="min-h-screen pb-20" data-testid="destination-page">
      <section className="relative pt-32 pb-16 bg-[#2D1037] overflow-hidden">
        <img src={imgSrc(lead.image_url)} onError={(e) => { e.currentTarget.style.display = 'none'; }} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D1037] via-[#2D1037]/90 to-transparent" />
        <div className="relative container-custom">
          <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#E5C178] mb-8 hover:text-[#C5A059]"><ArrowLeft size={16} /> Back to shop</Link>
          <span className="eyebrow mb-4 text-[#C5A059]"><span className="w-10 h-px bg-[#C5A059]" /> {lead.origin} → {lead.destination}</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#FAF7F2] mb-3" data-testid="destination-title">Moving to {lead.destination}</h1>
          <p className="font-display text-2xl text-[#E5C178] italic mb-3">{lead.guide_title}</p>
          <p className="max-w-2xl text-[#FAF7F2]/75 font-light leading-relaxed mb-6" data-testid="destination-intro">{lead.guide_subtitle}</p>
          {facts.length > 0 && <ul className="mb-8 space-y-1">{facts.map((f) => <li key={f} className="text-sm text-[#FAF7F2]/60">· {f}</li>)}</ul>}
          <div className="flex flex-wrap items-center gap-6">
            {lead.sample_url && (
              <button onClick={() => setSample(lead)} data-testid="destination-sample-btn" className="btn-gold text-sm inline-flex items-center gap-2"><FileText size={15} /> Read a free sample</button>
            )}
            <ShareBar url={`${BACKEND}/api/share/destinations/${slug}`} text={`Moving from Australia to ${lead.destination}: ${lead.guide_title}`} testId="destination-share-bar" />
          </div>
        </div>
      </section>
      <div className="container-custom pt-16">
        <Group title="The complete guide" items={full} onBuy={setBuying} testId="destination-full" />
        <Group title="Individual volumes & tools" items={parts} onBuy={setBuying} testId="destination-parts" />
        <FaqSection slug={slug} destination={lead.destination} />
        <div className="max-w-3xl mx-auto"><DestinationAlertForm dark title="Considering another country too?" /></div>
      </div>
      <PaymentModal product={buying} onClose={() => setBuying(null)} />
      <SampleModal product={sample} onClose={() => setSample(null)} />
    </div>
  );
}
